/**
 * Seeding Calculator Logic
 * Calculates optimal seeding windows based on grass type, location, and frost dates
 */

import { GRASS_CHARACTERISTICS } from '../data/grass-characteristics'
import { getRegionFromZipCode, REGIONAL_CLIMATE_DATA } from '../data/regional-climate'
import { GERMINATION_ROWS } from '../data/soil-germination-temps'
import { parseFrostDate, addDays, getWindowStatus } from './date-utils'
import { fallOverseedCall, fallOverseedWindow, type FallOverseedCall } from './fall-overseed'
import { doyOf, frostNormalsForZip, type FrostNormals } from './frost-normals'
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from '@/lib/lawn-plan-engine'

export interface SeedingWindow {
  season: 'spring' | 'fall'
  optimalStart: Date
  optimalEnd: Date
  acceptableStart: Date
  acceptableEnd: Date
  tooLateAfter: Date
}

export interface SeedingResult extends DeterministicToolResultMetadata {
  // Input context
  grassType: string
  grassSeason: 'Cool' | 'Warm'
  region: string
  zipCode: string
  checkDate: Date
  
  // Answer
  canSeedNow: boolean
  status: 'optimal' | 'acceptable' | 'too_late' | 'wrong_season'
  statusMessage: string
  statusColor: 'green' | 'yellow' | 'red' | 'gray'
  
  // Windows
  primaryWindow: SeedingWindow | null
  secondaryWindow: SeedingWindow | null
  
  // Regional data
  regionalData: {
    regionName: string
    firstFrost: string
    lastFrost: string
    climateZone: string
  }
  
  // Recommendations
  recommendations: {
    immediate: string[]
    alternative: string[]
    nextSteps: string[]
  }
  seedQuantityEstimate?: {
    lawnSizeSqft: number
    newLawnPounds: string
    overseedPounds: string
  }
  /**
   * The location's first-frost normals (NOAA 1991-2020 by ZIP prefix, else
   * the region's range). Cool-season results only; the verdict layer re-runs
   * the shared fall overseed call with a soil reading when it has one.
   */
  frostNormals?: FrostNormals | null
  /** The shared fall overseed call on `checkDate`, calendar only. Cool-season results only. */
  fallOverseed?: FallOverseedCall | null
}

/**
 * Calculate seeding windows for cool-season grasses
 * Fall is primary, spring is secondary. The fall window is the shared fall
 * overseed rule's (lib/fall-overseed.ts): good until the species' lead time
 * before the median first frost, then late but possible.
 */
function calculateCoolSeasonWindows(
  grassType: string,
  frost: FrostNormals | null,
  year: number,
  lastFrost: Date,
): {
  primary: SeedingWindow
  secondary: SeedingWindow
} {
  // FALL SEEDING (Primary - Best)
  const fall = fallOverseedWindow(grassType, frost, year)
  const fallOptimalStart = fall.opensOn
  const fallOptimalEnd = fall.goodUntil
  const fallAcceptableStart = fall.goodUntil
  const fallAcceptableEnd = fall.lateUntil
  const fallTooLate = fall.lateUntil

  // SPRING SEEDING (Secondary - OK)
  const springOptimalStart = lastFrost                 // Right after last frost
  const springOptimalEnd = addDays(lastFrost, 30)      // 30 days after
  const springAcceptableStart = addDays(lastFrost, 30) // Same as optimal end
  const springAcceptableEnd = addDays(lastFrost, 60)   // 60 days after
  const springTooLate = addDays(lastFrost, 60)

  return {
    primary: {
      season: 'fall',
      optimalStart: fallOptimalStart,
      optimalEnd: fallOptimalEnd,
      acceptableStart: fallAcceptableStart,
      acceptableEnd: fallAcceptableEnd,
      tooLateAfter: fallTooLate
    },
    secondary: {
      season: 'spring',
      optimalStart: springOptimalStart,
      optimalEnd: springOptimalEnd,
      acceptableStart: springAcceptableStart,
      acceptableEnd: springAcceptableEnd,
      tooLateAfter: springTooLate
    }
  }
}

/**
 * Calculate seeding windows for warm-season grasses
 * Spring/early summer is primary, no fall seeding
 */
function calculateWarmSeasonWindows(lastFrost: Date): {
  primary: SeedingWindow
  secondary: null
} {
  // SPRING/EARLY SUMMER SEEDING (Primary - Only Option)
  const springOptimalStart = addDays(lastFrost, 14)    // 2 weeks after last frost
  const springOptimalEnd = addDays(lastFrost, 60)      // 60 days after
  const springAcceptableStart = addDays(lastFrost, 60) // Same as optimal end
  const springAcceptableEnd = addDays(lastFrost, 90)   // 90 days after
  const springTooLate = addDays(lastFrost, 90)

  return {
    primary: {
      season: 'spring',
      optimalStart: springOptimalStart,
      optimalEnd: springOptimalEnd,
      acceptableStart: springAcceptableStart,
      acceptableEnd: springAcceptableEnd,
      tooLateAfter: springTooLate
    },
    secondary: null
  }
}

/**
 * Determine current seeding status based on date and windows
 */
function determineCurrentStatus(
  currentDate: Date,
  primaryWindow: SeedingWindow,
  secondaryWindow: SeedingWindow | null,
  grassSeason: 'Cool' | 'Warm'
): {
  canSeedNow: boolean
  status: 'optimal' | 'acceptable' | 'too_late' | 'wrong_season'
  statusMessage: string
  statusColor: 'green' | 'yellow' | 'red' | 'gray'
} {
  // Check primary window
  const primaryStatus = getWindowStatus(
    currentDate,
    primaryWindow.optimalStart,
    primaryWindow.optimalEnd,
    primaryWindow.acceptableEnd
  )

  if (primaryStatus === 'optimal') {
    return {
      canSeedNow: true,
      status: 'optimal',
      statusMessage: `Yes! You're in the OPTIMAL seeding window!`,
      statusColor: 'green'
    }
  }

  if (primaryStatus === 'acceptable') {
    return {
      canSeedNow: true,
      status: 'acceptable',
      statusMessage: `You can seed, but act quickly!`,
      statusColor: 'yellow'
    }
  }

  // Check secondary window if exists (cool-season grasses)
  if (secondaryWindow) {
    const secondaryStatus = getWindowStatus(
      currentDate,
      secondaryWindow.optimalStart,
      secondaryWindow.optimalEnd,
      secondaryWindow.acceptableEnd
    )

    if (secondaryStatus === 'optimal') {
      return {
        canSeedNow: true,
        status: 'optimal',
        statusMessage: `Yes! You're in a good seeding window!`,
        statusColor: 'green'
      }
    }

    if (secondaryStatus === 'acceptable') {
      return {
        canSeedNow: true,
        status: 'acceptable',
        statusMessage: `You can seed, but it's not ideal`,
        statusColor: 'yellow'
      }
    }
  }

  // After the year-aware picker resolves windows, neither primary nor secondary should
  // ever be 'too_late' (they always point to the next relevant occurrence). The user is
  // waiting for the upcoming window to open. Build a forward-looking message.
  const upcoming = [primaryWindow, secondaryWindow]
    .filter((w): w is SeedingWindow => Boolean(w) && currentDate < w!.optimalStart)
    .sort((a, b) => a.optimalStart.getTime() - b.optimalStart.getTime())

  if (upcoming.length > 0) {
    const next = upcoming[0]
    const days = Math.max(
      0,
      Math.round((next.optimalStart.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))
    )
    const seasonLabel = next.season === 'fall' ? 'fall' : 'spring'
    return {
      canSeedNow: false,
      status: 'too_late',
      statusMessage:
        days > 30
          ? `Wait for the ${seasonLabel} seeding window — about ${days} days away`
          : `The ${seasonLabel} seeding window opens in ${days} day${days === 1 ? '' : 's'}`,
      statusColor: 'gray'
    }
  }

  // Genuinely past everything (rare with year-aware picker — can happen if all windows
  // have edge-case parsing).
  return {
    canSeedNow: false,
    status: 'too_late',
    statusMessage: `Too late for this season`,
    statusColor: 'red'
  }
}

/**
 * Generate recommendations based on current status
 */
function generateRecommendations(
  status: 'optimal' | 'acceptable' | 'too_late' | 'wrong_season',
  grassType: string,
  grassSeason: 'Cool' | 'Warm',
  primaryWindow: SeedingWindow,
  secondaryWindow: SeedingWindow | null
): {
  immediate: string[]
  alternative: string[]
  nextSteps: string[]
} {
  const immediate: string[] = []
  const alternative: string[] = []
  const nextSteps: string[] = []

  if (status === 'optimal') {
    immediate.push(`This is the perfect time to seed ${grassType}!`)
    immediate.push(`Prepare your soil by removing debris and loosening the top 2-3 inches`)
    immediate.push(`Apply a starter fertilizer before seeding`)
    immediate.push(`Keep the soil consistently moist for 2-3 weeks after seeding`)
    
    nextSteps.push(`Water lightly 2-3 times daily until germination`)
    nextSteps.push(`Reduce watering frequency but increase depth after grass reaches 1 inch`)
    nextSteps.push(`First mowing when grass reaches ${grassSeason === 'Cool' ? '3-4 inches' : '2-3 inches'}`)
  }

  if (status === 'acceptable') {
    immediate.push(`Seed as soon as possible - you're at the edge of the window`)
    immediate.push(`Consider using a higher seeding rate (10-20% more) to compensate`)
    immediate.push(`Prepare soil thoroughly - time is limited for establishment`)
    immediate.push(`Use a starter fertilizer to promote faster growth`)
    
    alternative.push(`Consider using sod instead of seed for faster establishment`)
    alternative.push(`Overseed heavily to increase success rate`)
    
    nextSteps.push(`Monitor weather closely - protect from early frost if needed`)
    nextSteps.push(`Be prepared for slower germination in cooler temps`)
  }

  if (status === 'too_late') {
    alternative.push(`Wait for the next optimal seeding window`)
    if (secondaryWindow) {
      alternative.push(`Next window: ${secondaryWindow.season} season starting ${secondaryWindow.optimalStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`)
    }
    alternative.push(`Consider using sod for immediate results`)
    alternative.push(`Focus on improving soil quality while you wait`)
    
    nextSteps.push(`Conduct a soil test to determine pH and nutrient needs`)
    nextSteps.push(`Add organic matter or compost to improve soil structure`)
    nextSteps.push(`Remove weeds and debris in preparation for next window`)
    nextSteps.push(`Plan your seeding project for the optimal window`)
  }

  return { immediate, alternative, nextSteps }
}

function generateCarpetgrassRecommendations(): {
  immediate: string[]
  alternative: string[]
  nextSteps: string[]
} {
  const row = GERMINATION_ROWS.find((entry) => entry.grass === 'Carpetgrass')
  const soilMinimum = row?.minF ?? 69

  return {
    immediate: [
      `If you seed Carpetgrass, wait until soil reaches ${soilMinimum}°F or warmer.`,
    ],
    alternative: [
      'Use the seed label for the rate and establishment instructions.',
    ],
    nextSteps: [
      'The frost-date window is a calendar estimate only. It is not an exact Carpetgrass planting date.',
    ],
  }
}

/**
 * Main calculator function
 * Calculates seeding windows and current status
 */
export function calculateSeedingWindows(
  grassType: string,
  zipCode: string,
  currentDate: Date = new Date(),
  options?: { lawnSize?: string | number | null; sunlight?: string | null },
): SeedingResult {
  const context = buildCanonicalToolContext({
    toolId: 'seeding',
    grassType,
    lawnSize: options?.lawnSize,
    sunlight: options?.sunlight,
  })

  // 2. Get regional data
  const region = getRegionFromZipCode(zipCode)
  if (region === 'Unknown') {
    throw new Error('We could not determine your region from this ZIP code. Try a nearby ZIP.')
  }

  const climate = REGIONAL_CLIMATE_DATA[region]
  if (!climate) {
    throw new Error('Climate data is not available for your region.')
  }

  // 1. Handle "Other/Not Listed" - provide general cool-season guidance
  let grass: any
  let isOtherType = false

  const engineGrassType = grassType === "Other/Not Listed" ? grassType : context.grassType

  if (engineGrassType === "Other/Not Listed") {
    isOtherType = true
    // Use Kentucky Bluegrass as default cool-season representative
    grass = {
      name: "Other/Not Listed",
      season: 'Cool' as const,
    }
  } else {
    grass = GRASS_CHARACTERISTICS[engineGrassType]
    if (!grass) {
      throw new Error('Seeding data is not available for this grass type.')
    }
  }

  // 3. Parse frost dates for both this calendar year and next, then pick the
  // year that's currently relevant per window. Previously this used
  // `getCurrentGrowingYear()` which bumps to next year after September —
  // collapsing fall and spring onto a single year so a Sept-Dec user lost the
  // current fall window entirely. Same bug class as the herbicide picker.
  // Conservative approach for frost dates: use latest first frost and earliest
  // last frost (narrows windows by ~1-2 weeks on each end; the "acceptable"
  // window provides buffer).
  const thisYear = currentDate.getFullYear()
  const nextYear = thisYear + 1

  const frost = grass.season === 'Cool' ? frostNormalsForZip(zipCode) ?? regionFrostNormals(climate.firstFrost) : null

  function buildWindowsForYear(yr: number) {
    const lf = parseFrostDate(climate.lastFrost, yr, false)
    return grass.season === 'Cool'
      ? calculateCoolSeasonWindows(engineGrassType, frost, yr, lf)
      : calculateWarmSeasonWindows(lf)
  }

  const thisYr = buildWindowsForYear(thisYear)
  const nextYr = buildWindowsForYear(nextYear)

  // Primary window: this year's if not yet too late, else next year's.
  const primaryWindow: SeedingWindow =
    currentDate <= thisYr.primary.tooLateAfter ? thisYr.primary : nextYr.primary

  // Secondary window (cool-season only): same logic.
  let secondaryWindow: SeedingWindow | null = null
  if (grass.season === 'Cool' && thisYr.secondary && nextYr.secondary) {
    secondaryWindow =
      currentDate <= thisYr.secondary.tooLateAfter ? thisYr.secondary : nextYr.secondary
  }

  // 5. Determine current status
  const statusInfo = determineCurrentStatus(
    currentDate,
    primaryWindow,
    secondaryWindow,
    grass.season
  )

  // Carpetgrass timing cannot be confirmed from frost dates alone. Keep the
  // calculated window for callers that depend on the result shape, but fail
  // closed until the user checks the soil temperature.
  const safeStatusInfo = engineGrassType === 'Carpetgrass'
    ? {
        canSeedNow: false,
        status: 'wrong_season' as const,
        statusMessage: `If you seed Carpetgrass, wait until soil reaches ${GERMINATION_ROWS.find((entry) => entry.grass === 'Carpetgrass')?.minF ?? 69}°F or warmer. Frost-date timing is a calendar estimate only; follow the seed label.`,
        statusColor: 'gray' as const,
      }
    : statusInfo

  // 6. Generate recommendations
  const recommendations = engineGrassType === 'Carpetgrass'
    ? generateCarpetgrassRecommendations()
    : generateRecommendations(
        safeStatusInfo.status,
        engineGrassType,
        grass.season,
        primaryWindow,
        secondaryWindow
      )
  
  // Add note for "Other" grass types
  if (isOtherType) {
    recommendations.immediate.unshift(
      "⚠️ General cool-season grass guidance provided. For best results, identify your specific grass type."
    )
    recommendations.alternative.push(
      "Take our grass identification quiz to get personalized recommendations"
    )
  }

  // 7. Return complete result
  return attachToolEngineMetadata({
    grassType: engineGrassType,
    grassSeason: grass.season,
    region,
    zipCode,
    checkDate: currentDate,
    canSeedNow: safeStatusInfo.canSeedNow,
    status: safeStatusInfo.status,
    statusMessage: safeStatusInfo.statusMessage,
    statusColor: safeStatusInfo.statusColor,
    primaryWindow,
    secondaryWindow,
    regionalData: {
      regionName: region,
      firstFrost: climate.firstFrost,
      lastFrost: climate.lastFrost,
      climateZone: climate.zone
    },
    recommendations,
    seedQuantityEstimate: buildSeedQuantityEstimate(grass, context.lawnSizeSqft),
    ...(grass.season === 'Cool'
      ? {
          frostNormals: frost,
          fallOverseed: fallOverseedCall({ grassType: engineGrassType, now: currentDate, frost }),
        }
      : {}),
  }, context)
}

/**
 * Frost normals from a region's first-frost range ("October 1-15"), used only
 * when the ZIP prefix has no NOAA normals: the middle of the range.
 */
function regionFrostNormals(firstFrost: string): FrostNormals | null {
  if (!firstFrost || /rare/i.test(firstFrost)) return { zip3: 0, first32Doy: null, first28Doy: null }
  const year = 2025
  const early = parseFrostDate(firstFrost, year, false)
  const late = parseFrostDate(firstFrost, year, true)
  if (Number.isNaN(early.getTime()) || Number.isNaN(late.getTime())) return null
  const mid = Math.round((doyOf(early) + doyOf(late)) / 2)
  return { zip3: 0, first32Doy: mid, first28Doy: mid + 12 }
}

function buildSeedQuantityEstimate(
  grass: { seedingRate?: { newLbsPer1000Min: number; newLbsPer1000Max: number; overseedLbsPer1000Min: number; overseedLbsPer1000Max: number } },
  lawnSizeSqft: number | null,
): SeedingResult['seedQuantityEstimate'] {
  if (!lawnSizeSqft || lawnSizeSqft <= 0 || !grass.seedingRate) return undefined
  const multiplier = lawnSizeSqft / 1000
  const fmt = (value: number) => Number(value.toFixed(1)).toLocaleString()
  return {
    lawnSizeSqft,
    newLawnPounds: `${fmt(grass.seedingRate.newLbsPer1000Min * multiplier)}-${fmt(grass.seedingRate.newLbsPer1000Max * multiplier)} lb`,
    overseedPounds: `${fmt(grass.seedingRate.overseedLbsPer1000Min * multiplier)}-${fmt(grass.seedingRate.overseedLbsPer1000Max * multiplier)} lb`,
  }
}
